import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import {X} from 'lucide-react';
import {useTheme} from '@mui/material/styles';
import {
  DARK_TEXT_COLOR,
  WARNING_BG,
  GREY_BORDER,
  TEAL_PRIMARY,
} from '../../theme';
import WarningIcon from '../../assets/alert-triangle.svg';
import {abortCurrentStream} from '../../data/agentChatService';

const ExitProofreadingDialog = ({
  open,
  onClose,
  onRemoveFixes,
  remainingCount,
}) => {
  const theme = useTheme();
  const handleRemoveFixes = () => {
    abortCurrentStream();
    onRemoveFixes();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="exit-proofreading-dialog"
      sx={{
        '& .MuiPaper-root': {
          width: '400px',
          borderRadius: '12px',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
        }}
      >
        <Typography sx={{fontWeight: 600, fontSize: 16}}>
          Are you sure you want to exit?
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <X size={20} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: WARNING_BG,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <img src={WarningIcon} alt="Warning" width={24} height={24} />
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'column'}}>
          <Typography
            sx={{fontWeight: 600, fontSize: 14, lineHeight: 1.5, mb: 0.25}}
          >
            You have {remainingCount}{' '}
            {remainingCount === 1 ? 'suggestion' : 'suggestions'} left.
          </Typography>
          <Typography sx={{color: DARK_TEXT_COLOR, fontSize: 14}}>
            Navigating away will discard all suggested edits. Accept or reject
            them before leaving, or request a new proofread later.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{px: 3, py: 2, gap: 1, borderTop: `1px solid ${GREY_BORDER}`}}
      >
        <Button
          variant="contained"
          onClick={handleRemoveFixes}
          size="small"
          sx={{...theme.button.secondary.medium, flexShrink: 0}}
        >
          Discard {remainingCount}{' '}
          {remainingCount === 1 ? 'suggestion' : 'suggestions'}
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          size="small"
          sx={{
            backgroundColor: TEAL_PRIMARY,
            borderRadius: '6px',
            height: '32px',
            flex: 1,
            '&:hover': {
              backgroundColor: TEAL_PRIMARY,
            },
          }}
        >
          Continue review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExitProofreadingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemoveFixes: PropTypes.func.isRequired,
  remainingCount: PropTypes.number.isRequired,
};

export default ExitProofreadingDialog;
